// src/App.js
import React, { useEffect, useState, useMemo } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth, db } from './firebaseConfig'
import { doc, getDoc } from 'firebase/firestore'


// Pages (existing)
import DashboardPage from './DashboardPage'
import LoginPage from './LoginPage'
import UsersPage from './pages/UsersPage'
import UserDetailPage from './pages/UserDetailPage'
import InboxPage from './pages/InboxPage'
import SettingsPage from './pages/SettingsPage'
import RatingsPage from './pages/RatingsPage'
import DriverApprovalPage from './pages/DriverApprovalPage'

// ❌ DUO FILE INI DIHAPUS IMPORTNYA KARENA FILENYA SUDAH TIDAK ADA
// import CleanerDetailPage from './pages/CleanerDetailPage' 
// import CleanerServicesPage from './pages/CleanerServicesPage'

import TherapistDetailPage from './pages/TherapistDetailPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import ChatViewerPage from './pages/ChatViewerPage'
import FinancePage from './pages/finance/FinancePage'
import TherapistReferralsPage from './pages/TherapistReferralsPage'
import DriversPage from './pages/DriversPage'

// New list/services pages
import TherapistsPage from './pages/TherapistsPage'
import TherapistServicesPage from './pages/TherapistServicesPage'

// ✅ KITA PERTAHANKAN INI (Halaman List Utama)
import CleanersPage from './pages/CleanersPage'

// Halaman Laporan / Reports
import ReportsPage from './pages/ReportsPage'
import PartnerPerformancePage from './pages/PartnerPerformancePage' // ✨ BARU: Import halaman performa

// Admin pages
import AdminListPage from './pages/admin/AdminListPage'
import AddAdminPage from './pages/admin/AddAdminPage'
import AdminRolesPage from './pages/admin/AdminRolesPage'
import EditRolePermissionPage from './pages/admin/EditRolePermissionPage'

import ManageTherapistServicesPage from './pages/therapists/ManageTherapistServicesPage'
import TherapistRatingPage from './pages/therapists/TherapistRatingPage'
import GlobalChatNotifier from './GlobalChatNotifier'

// -----------------------------------------------------------
// 🔐 ProtectedRoute — using Permission System (no hardcode role)
// -----------------------------------------------------------
function ProtectedRoute({ user, perms, permKey, children }) {
  if (!user) return <Navigate to="/login" replace />

  // If perm required but user doesn’t have it → block
  if (permKey && !perms?.[permKey]) {
    return (
      <div className="p-10 text-red-400 text-center text-xl font-bold">
        🚫 Anda tidak memiliki akses ke halaman ini.
      </div>
    )
  }

  return children
}

export default function App() {
  const [user, setUser] = useState(null)
  const [perms, setPerms] = useState(null)
  const [loading, setLoading] = useState(true)

  // default permission shape
  const defaultPerms = useMemo(
    () => ({
      dashboard: false,
      users: false,
      therapists: false,
      therapist_services: false,
      cleaners: false,
      cleaner_services: false,
      admin_management: false,
      area_coverage: false,
      finance: false,
      orders: false,
      reports: false,
      drivers: false,
      inbox: false,
      settings: false,
    }),
    []
  )

  

  // Auth state + permission load
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null)
        setPerms(null)
        setLoading(false)
        return
      }

      try {
        const adminRef = doc(db, 'admins', currentUser.uid)
        const snap = await getDoc(adminRef)

        if (!snap.exists()) {
          alert('Akun ini tidak terdaftar sebagai admin.')
          await signOut(auth)
          setUser(null)
          setPerms(null)
          setLoading(false)
          return
        }

        const adminData = snap.data()
        const roleName = adminData.role

        // Load permission doc
        const permRef = doc(db, 'admin_roles', roleName)
        const permSnap = await getDoc(permRef)

        let loadedPerms = null

        if (!permSnap.exists()) {
          // Super Admin fallback → full true
          if (roleName === 'super_admin') {
            loadedPerms = Object.fromEntries(
              Object.keys(defaultPerms).map((k) => [k, true])
            )
          } else {
            loadedPerms = { ...defaultPerms }
          }
        } else {
          loadedPerms = { ...defaultPerms, ...permSnap.data() }
        }

        setUser({
          ...currentUser,
          adminName: adminData.name,
          role: roleName,
        })

        setPerms(loadedPerms)
      } catch (err) {
        console.error("Error validasi admin:", err)
        // Tambahkan alert agar kamu tahu jika database bermasalah
        alert("Gagal memuat data Admin: " + err.message) 
        setUser(null)
        setPerms(null)
      }

      setLoading(false)
    })

    return () => unsub()
  }, [defaultPerms])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-400">
        Loading...
      </div>
    )
  }

  return (
    <Router>
      {/* Pindahkan GlobalChatNotifier ke dalam sebuah div pembungkus yang menampung Routes */}
      <div className="relative">
        
        {/* Pastikan Notifikasi selalu di-render jika user ada */}
        {user && <GlobalChatNotifier />}
        
        <Routes>
          {!user ? (
            <Route path="*" element={<LoginPage />} />
          ) : (
            <>
            {/* Dashboard */}
            <Route
              path="/"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="dashboard">
                  <DashboardPage user={user} onLogout={() => signOut(auth)} />
                </ProtectedRoute>
              }
            />

            {/* Users */}
            <Route
              path="/users"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="users">
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="users">
                  <UserDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Orders */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="orders">
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="orders">
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId/chat"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="orders">
                  <ChatViewerPage />
                </ProtectedRoute>
              }
            />

            {/* Ratings */}
            <Route
              path="/ratings"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="reports">
                  <RatingsPage />
                </ProtectedRoute>
              }
            />

            {/* Reports Dashboard */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="reports">
                  <ReportsPage />
                </ProtectedRoute>
              }
            />

            {/* ✨ BARU: Halaman Performa Mitra */}
            <Route
              path="/performa-mitra"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="reports">
                  <PartnerPerformancePage />
                </ProtectedRoute>
              }
            />

            {/* Cleaners (HANYA LIST UTAMA) */}
            <Route
              path="/cleaners"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="cleaners">
                  <CleanersPage />
                </ProtectedRoute>
              }
            />
            
            {/* ❌ ROUTE DETAIL & SERVICE CLEANER DIHAPUS */}

            {/* Therapists */}
            <Route
              path="/therapists"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="therapists">
                  <TherapistsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/therapists/:mitraId"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="therapists">
                  <TherapistDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Services */}
            <Route
              path="/therapist-services"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="therapist_services"
                >
                  <TherapistServicesPage />
                </ProtectedRoute>
              }
            />
            {/* Manage Therapist Services (Add/Edit Services untuk 1 Terapis) */}
            <Route
              path="/therapists/manage-services"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="therapist_services"
                >
                  <ManageTherapistServicesPage />
                </ProtectedRoute>
              }
            />

            {/* Therapist Rating Editor */}
            <Route
              path="/therapists/rating"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="therapists">
                  <TherapistRatingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/therapists/referrals"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="therapists">
                  <TherapistReferralsPage />
                </ProtectedRoute>
              }
            />
            {/* Drivers */}
            <Route
              path="/drivers"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="drivers">
                  <DriversPage user={user} onLogout={() => signOut(auth)} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drivers/approve/:id"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="drivers">
                  <DriverApprovalPage user={user} onLogout={() => signOut(auth)} />
                </ProtectedRoute>
              }
            />

            {/* Inbox */}
            <Route
              path="/inbox"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="inbox">
                  <InboxPage />
                </ProtectedRoute>
              }
            />
            

            {/* Finance */}
            <Route
              path="/finance"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="finance">
                  <FinancePage />
                </ProtectedRoute>
              }
            />

            {/* Settings */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute user={user} perms={perms} permKey="settings">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Management */}
            <Route
              path="/admin-management"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="admin_management"
                >
                  <AdminListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-management/add"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="admin_management"
                >
                  <AddAdminPage currentAdmin={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-management/roles"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="admin_management"
                >
                  <AdminRolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-management/roles/:roleName"
              element={
                <ProtectedRoute
                  user={user}
                  perms={perms}
                  permKey="admin_management"
                >
                  <EditRolePermissionPage />
                </ProtectedRoute>
              }
            />

            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        )}
      </Routes>
      
      </div> {/* [BARU] Tambahkan tag penutup div di sini */}
      
    </Router>
  )
}